import { AnimatePresence, motion } from 'motion/react';
import { AlertCircle, BookOpen, Check, FileText, Link2, Search, Stethoscope, Wrench, type LucideIcon } from 'lucide-react';
import type { ToolActivity } from '@medicail/shared';
import { cn } from '@/lib/cn';
import { Spinner } from '@/components/ui';

const ICONS: Record<string, LucideIcon> = {
  web_search: Search,
  fetch_page: FileText,
  get_authoritative_sources: BookOpen,
  get_curated_links: Link2,
  find_doctors: Stethoscope,
};

function labelFor(tool: ToolActivity): string {
  if (tool.label) return tool.label;
  switch (tool.name) {
    case 'web_search':
      return 'Searching the web';
    case 'find_doctors':
      return 'Finding top doctors';
    case 'fetch_page':
      return 'Reading a page';
    case 'get_authoritative_sources':
      return 'Checking clinical guidelines';
    default:
      return 'Working';
  }
}

export function ToolPills({ tools }: { tools: ToolActivity[] }) {
  if (!tools.length) return null;
  return (
    <ul className="mb-3 flex flex-wrap gap-2" aria-label="Assistant activity">
      <AnimatePresence initial={false}>
        {tools.map((t) => {
          const Icon = ICONS[t.name] ?? Wrench;
          const running = t.status === 'running';
          const text = running && !/(\.\.\.|…)$/.test(labelFor(t)) ? `${labelFor(t)}...` : labelFor(t);
          return (
            <motion.li
              key={t.toolUseId}
              layout
              initial={{ opacity: 0, scale: 0.92, y: 4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className={cn(
                'inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-[0.8rem]',
                running
                  ? 'animate-shimmer border-royal-200 bg-royal-50 text-royal-700 dark:border-royal-500/40 dark:bg-royal-500/15 dark:text-royal-200'
                  : t.status === 'error'
                    ? 'border-danger-500/30 bg-danger-50 text-danger-600 dark:bg-red-500/10 dark:text-red-300'
                    : 'border-line bg-surface-muted text-ink-muted',
              )}
            >
              {running ? <Spinner /> : t.status === 'error' ? <AlertCircle className="size-3.5" /> : <Check className="size-3.5 text-leaf-600 dark:text-green-300" />}
              {!running && <Icon className="size-3.5 shrink-0" aria-hidden="true" />}
              <span className="truncate">{text}</span>
            </motion.li>
          );
        })}
      </AnimatePresence>
    </ul>
  );
}
